import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TabPosition } from '@progress/kendo-angular-layout';
import { Observable } from 'rxjs';
import { DataWithPagingMetadata } from '~/infrastructure/paging';
import { PageNames } from '~/models/pageNames';
import { PageState } from '~/shared/enums/stock-files.enum';
import { fileSources } from '~/shared/enums/fileSources';
import {
  MaintIntervalType,
  MaintIntervalTypeLookupDto,
  MaintenanceAssetScheduleDto,
  MaintenanceAssetScheduleRequestModel,
  MaintenanceIntervalDto,
  MaintenanceTypeDto,
  MaintenanceScheduleStockDto
} from '~/models/modules/Asset/Transactions/Maintenance/maintenance.model';
import { FormatService } from '~/services/administration.ts/format';
import { UsersService } from '~/services/administration.ts/users';
import { AssetService } from '~/services/modules/assets/asset.service';
import { ScheduleMaintenanceService } from '~/services/modules/assets/transactions/schedule-maintenance.service';
import { SharedService } from '~/services/sharedService';
import { GDataService } from '~/services/GDataService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { NoteComponent } from '~/shared/components/note/note.component';
import { FilesService } from '~/services/modules/stocks/files';
import { StockItemsService } from '~/services/modules/stocks/items';
import { BarcodeGeneratorsService } from '~/services/modules/stocks/barcode-generators';
import { EnvironmentLoaderService } from '~/services/BackEndURL/environment-loader.service';

/**
 * @component ScheduleMaintenanceFormComponent
 * @description
 * Enterprise V2 Modal Form Dialog for creating and modifying preventative & recurring
 * asset maintenance schedules.
 *
 * Architecture & Features:
 * 1. Standard TabStrip & Notes Sidebar Design:
 *    - Left Panel: TabStrip with Documents tab.
 *    - Right Panel: Notes Sidebar (NoteComponent).
 * 2. Infinite-Scroll / Lazy-Loaded Asset ComboBox:
 *    - Mimics the canonical MoveComponent pattern.
 *    - Uses Renderer2 to attach scroll listeners to `.k-popup .k-list-content`.
 *    - Pages 50 assets at a time dynamically as the user scrolls.
 *    - Debounces search input (500ms) and resets pagination seamlessly.
 *    - Retains preselected asset in the collection to prevent display blanks.
 * 3. Timezone-Aware Scheduled Date Constraints:
 *    - Computes `_minDateToday` using `_sharedService.getTodayInUserTimezone()`.
 *    - Disallows selecting past dates when creating new schedules.
 *    - Automatically permits historical dates when editing pre-existing schedules to prevent validation locks.
 * 4. Reactive Recurrence Logic:
 *    - Dynamically toggles validators between One-Time (`scheduledFor`) and Recurring (`frequency`, `maintenanceIntervalId`, `startDate`).
 * 5. Integrated Lookups:
 *    - Asset selector grid dialog popup.
 *    - Person selector grid dialog popup.
 *    - Maintenance Type quick-creation modal.
 *    - Documents upload & library picker modals.
 */
@Component({
    selector: 'app-schedule-maintenance-form',
    templateUrl: './schedule-maintenance-form.component.html',
    styleUrls: ['./schedule-maintenance-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScheduleMaintenanceFormComponent implements OnInit, OnDestroy {
  @ViewChild('mainWindow') kendoMainWindow: any;
  @ViewChild('note') note: NoteComponent;
  @Input() schedule: MaintenanceAssetScheduleDto | null = null;
  @Input() isEdit: boolean = false;
  @Output() saved = new EventEmitter<MaintenanceAssetScheduleDto>();
  @Output() cancelled = new EventEmitter<void>();

  _active: boolean = true;
  _theme: string = 'enterprise';
  _pageState: PageState = PageState.ADD;
  _page = PageNames.ScheduleMaintenance;
  _datePickerFormat: string = 'MM/dd/yyyy';

  _kendoTabStripPosition: TabPosition = 'left';
  _documentsPageName: string = 'documents';
  _selectedTab: string = 'Documents';

  // Section collapse states
  isGeneralInformationExpanded: boolean = true;
  isTimingExpanded: boolean = true;
  isDocumentsExpanded: boolean = true;
  isNotesExpanded: boolean = true;

  // Documents & Library State
  _scheduleFilesDoc: any[] = [];
  _selectedDocumentsKeys: any[] = [];
  _selectedDocuments: any[] = [];
  _selectedObjectDocuments: any[] = [];
  _showDocumentsGrid: boolean = false;
  _storagePath: string = '';
  _model: any = { id: 0 };
  _selectedAssetList: any[] = [];

  form: FormGroup;
  isSaving: boolean = false;

  // Dropdown data arrays
  maintenanceTypes: MaintenanceTypeDto[] = [];
  maintenanceTypesFiltered: MaintenanceTypeDto[] = [];
  intervals: any[] = [];
  intervalsFiltered: any[] = [];
  intervalTypes: MaintIntervalTypeLookupDto[] = [
    { id: MaintIntervalType.Days, name: 'Days', code: 'Days' },
    { id: MaintIntervalType.Months, name: 'Months', code: 'Months' },
    { id: MaintIntervalType.Hours, name: 'Hours', code: 'Hours' },
    { id: MaintIntervalType.Years, name: 'Years', code: 'Years' },
    { id: MaintIntervalType.Week, name: 'Week', code: 'Week' }
  ];
  intervalTypesFiltered: MaintIntervalTypeLookupDto[] = [...this.intervalTypes];
  persons: any[] = [];
  personsFiltered: any[] = [];
  assets: any[] = [];
  assetsFiltered: any[] = [];

  // Asset ComboBox Lazy Loading & Infinite Scroll state
  _assetComboBoxPagingParams = {
    currentItem: '0',
    totalbyPage: 50,
    search: '',
    page: 1
  };
  _allAssetCount: number = 0;
  _assetComboScroll: boolean = false;
  isOpened: boolean = false;
  clearTimeout: any;
  _minDateToday: Date = new Date();
  private _isLoadingAssets: boolean = false;
  private _assetFilterTimeout: any;
  private _scrollUnlisten?: () => void;

  // #region Asset Popup Selector (matching reserve popup pattern)
  _assetsListEnabled: boolean = false;
  _selectedAssets: any[] = [];
  // #endregion

  // #region Person Popup Selector (matching reserve popup pattern)
  _personsListEnabled: boolean = false;
  _selectedPerson: any = null;
  // #endregion

  // #region Stock Parts State
  _stockItemsListEnabled: boolean = false;
  _selectedStockItems: any[] = [];
  stockParts: MaintenanceScheduleStockDto[] = [];
  // #endregion

  // #region Maintenance Type Dialog (Enterprise V2 standard)
  _isDialiog: boolean = false;
  _dialogComponentName: string = '';
  _dialogModel: any = null;
  // #endregion

  constructor(
    private fb: FormBuilder,
    private scheduleService: ScheduleMaintenanceService,
    private assetService: AssetService,
    private usersService: UsersService,
    private _formatService: FormatService,
    private _themeManager: ThemeManagerService,
    private _sharedService: SharedService,
    private _dataService: GDataService,
    private layoutComponent: LayoutComponent,
    private _renderer2: Renderer2,
    private _filesService: FilesService,
    private _stockItemsService: StockItemsService,
    private _barcodeGeneratorsService: BarcodeGeneratorsService
  ) {
    this._themeManager.themeChange.subscribe((data) => {
      if (data) this._theme = data;
    });
  }

  /**
   * Component initialization lifecycle hook.
   * Resolves date format, model state, minimum date in user timezone, and loads dropdown options.
   */
  ngOnInit(): void {
    this._active = true;
    this._storagePath = EnvironmentLoaderService.envConfig?.enterpriseApiLocal
      ? EnvironmentLoaderService.envConfig.enterpriseApiLocal + '/'
      : '';

    if (this._formatService?.dateFormat) {
      this._datePickerFormat = this._formatService.dateFormat;
    }

    const sharedModel = this._sharedService?.model;
    let initialSchedule: any = null;
    let initialPageState: PageState = PageState.ADD;

    if (sharedModel) {
      if (sharedModel.model !== undefined) {
        initialSchedule = sharedModel.model;
      } else if (sharedModel.id !== undefined || sharedModel.maintenanceCode !== undefined || sharedModel.assetId !== undefined) {
        initialSchedule = sharedModel;
      }

      if (sharedModel.pageState !== undefined) {
        initialPageState = sharedModel.pageState;
      } else if (sharedModel.state !== undefined) {
        initialPageState = sharedModel.state;
      } else if (sharedModel.isNew === true) {
        initialPageState = PageState.ADD;
      } else if (initialSchedule?.id) {
        initialPageState = PageState.EDIT;
      }
    }

    if (this.schedule) {
      initialSchedule = this.schedule;
    }

    this._pageState = initialPageState;
    this.isEdit = this._pageState === PageState.EDIT || this._pageState === PageState.VIEW;
    this.schedule = initialSchedule || null;

    const scheduleId = this.schedule?.id || (this.schedule as any)?.maintenanceAssetScheduleId || sharedModel?.id || sharedModel?.itemId;

    if (scheduleId) {
      this._model = { id: scheduleId };
    }

    // Pre-seed dropdown collections immediately if schedule details are available
    if (this.schedule?.assetId) {
      const assetObj = this.schedule.asset || {
        id: this.schedule.assetId,
        assetNumber: this.schedule.assetNumber || `Asset #${this.schedule.assetId}`,
        assetName: this.schedule.assetName || ''
      };
      this.assets = [assetObj];
      this.assetsFiltered = [assetObj];
      this._selectedAssetList = [assetObj];
    }

    if (this.schedule?.maintenanceTypeId) {
      const typeObj: MaintenanceTypeDto = (this.schedule.maintenanceType as MaintenanceTypeDto) || {
        id: this.schedule.maintenanceTypeId,
        maintenanceTypeName: this.schedule.maintenanceTypeName || 'Maintenance Type',
        maintenanceTypeCode: this.schedule.maintenanceTypeCode || ''
      };
      this.maintenanceTypes = [typeObj];
      this.maintenanceTypesFiltered = [typeObj];
    }

    if (this.schedule?.personId) {
      const personObj = this.schedule.person || {
        id: this.schedule.personId,
        displayName: this.schedule.personName || `${this.schedule.person?.firstName || ''} ${this.schedule.person?.lastName || ''}`.trim() || `Person #${this.schedule.personId}`,
        fullName: this.schedule.personName || ''
      };
      this.persons = [personObj];
      this.personsFiltered = [personObj];
    }

    if (this.schedule?.maintenanceIntervalId) {
      const intObj = this.schedule.maintenanceInterval || {
        id: this.schedule.maintenanceIntervalId,
        name: this.schedule.maintenanceIntervalName || 'Interval',
        code: ''
      };
      this.intervals = [intObj];
      this.intervalsFiltered = [intObj];
    }

    this.initMinDate();
    this.initForm();
    if (this.schedule) {
      this.patchFormWithSchedule(this.schedule);
    }
    this.loadDropdownData();

    if (scheduleId) {
      this.scheduleService.getScheduleById(scheduleId).subscribe({
        next: (data: any) => {
          const detail = data?.data || data;
          if (detail && detail.id) {
            this.schedule = { ...this.schedule, ...detail };
            this._model = { id: this.schedule.id };
            this.patchFormWithSchedule(this.schedule);
            if (this.schedule.assetId) {
              this.ensureAssetLoaded(this.schedule.assetId);
            }
            if (this.schedule.personId) {
              this.ensurePersonLoaded(this.schedule.personId);
            }
          }
        }
      });
    }
  }

  /**
   * Computes the minimum selectable date based on the user's active timezone.
   * Normalizes to 00:00:00 of today. When editing an older schedule, adjusts the
   * floor to permit the pre-existing historical date without validation errors.
   */
  private initMinDate(): void {
    const today = this._sharedService?.getTodayInUserTimezone
      ? this._sharedService.getTodayInUserTimezone()
      : new Date();
    today.setHours(0, 0, 0, 0);

    if (this.isEdit && this.schedule?.scheduledFor) {
      const pastDate = new Date(this.schedule.scheduledFor);
      if (pastDate < today) {
        this._minDateToday = pastDate;
        return;
      }
    }
    this._minDateToday = today;
  }

  /**
   * Component destruction lifecycle hook.
   * Cleans up active scroll listeners and pending debounce timers to prevent memory leaks.
   */
  ngOnDestroy(): void {
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    if (this._assetFilterTimeout) clearTimeout(this._assetFilterTimeout);
    if (this._scrollUnlisten) {
      this._scrollUnlisten();
      this._scrollUnlisten = undefined;
    }
  }

  /**
   * Responds to Kendo Window state events (minimized, maximized, restored).
   * @param e Window state change event.
   */
  _onStateChanges(e: any): void {
    const element: any = this.kendoMainWindow;
    if (!element?.el?.nativeElement) return;
    if (e === 'minimized' || e?.minimized) {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'maximized' || e?.maximized) {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else {
      element.el.nativeElement.classList.add('fullWidthFormForm');
    }
  }

  /**
   * Auto-generates a schedule maintenance barcode/code.
   */
  _generateBarcodeClick(): void {
    this._barcodeGeneratorsService.getBarcode('schedule-maintenance', 1).subscribe({
      next: (data: any) => {
        const val = data?.result ? data.result.toString() : (data?.data?.result ? data.data.result.toString() : (data?.data ? data.data.toString() : (Array.isArray(data) && data[0] ? data[0].toString() : '')));
        if (val) {
          this.form.patchValue({ maintenanceCode: val });
        }
      },
      error: () => {}
    });
  }

  /**
   * Initializes the reactive FormGroup with default values in the user's timezone.
   */
  private initForm(): void {
    const nowInUserTz = this._sharedService?.getTodayInUserTimezone
      ? this._sharedService.getTodayInUserTimezone()
      : new Date();

    const parseDate = (d: any): Date | null => {
      if (!d) return null;
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const isRec = Boolean(this.schedule?.isRecurring);
    const isTime = this.schedule?.timeInterval !== false;

    this.form = this.fb.group({
      id: [this.schedule?.id || null],
      maintenanceCode: [this.schedule?.maintenanceCode || ''],
      assetId: [this.schedule?.assetId || null, Validators.required],
      maintenanceTypeId: [this.schedule?.maintenanceTypeId || null, Validators.required],
      personId: [this.schedule?.personId || null],
      isRecurring: [isRec],
      timeInterval: [isTime],
      scheduledFor: [parseDate(this.schedule?.scheduledFor) || (isRec ? null : nowInUserTz)],
      scheduledForOtherInterval: [this.schedule?.scheduledForOtherInterval ?? null],
      frequency: [this.schedule?.frequency ?? 1, [Validators.min(1)]],
      maintenanceIntervalId: [this.schedule?.maintenanceIntervalId || null],
      maintIntervalType: [this.schedule?.maintIntervalType || MaintIntervalType.Days],
      scheduledBased: [this.schedule?.scheduledBased ?? false],
      startDate: [parseDate(this.schedule?.startDate) || (isRec ? nowInUserTz : null)],
      endDate: [parseDate(this.schedule?.endDate) || null],
      recurringBasis: [parseDate(this.schedule?.recurringBasis) || parseDate(this.schedule?.startDate) || (isRec ? nowInUserTz : null)],
      recurringBasisOtherInterval: [this.schedule?.recurringBasisOtherInterval ?? null],
      dueNext: [parseDate(this.schedule?.dueNext) || null],
      dueNextOthers: [this.schedule?.dueNextOthers ?? null],
      yearsToForcastStockQuantity: [this.schedule?.yearsToForcastStockQuantity ?? 1, [Validators.min(1)]],
      isFlagged: [Boolean(this.schedule?.isFlagged)],
      notes: [this.schedule?.notes || '']
    });

    if (!this.isEdit && !this.form.get('maintenanceCode')?.value) {
      this._generateBarcodeClick();
    }

    this.updateTimingValidatorsAndCalculations(false);
    this.form.get('isRecurring')?.valueChanges.subscribe(() => {
      this.updateTimingValidatorsAndCalculations(true);
    });
    this.form.get('timeInterval')?.valueChanges.subscribe(() => {
      this.updateTimingValidatorsAndCalculations(true);
    });
    this.form.get('frequency')?.valueChanges.subscribe(() => {
      this.calculateDueNext();
      this.recalculateAllStockForecasts();
    });
    this.form.get('maintIntervalType')?.valueChanges.subscribe(() => {
      this.calculateDueNext();
      this.recalculateAllStockForecasts();
    });
    this.form.get('startDate')?.valueChanges.subscribe((val) => {
      if (!this.form.get('recurringBasis')?.value) {
        this.form.patchValue({ recurringBasis: val }, { emitEvent: false });
      }
      this.calculateDueNext();
    });
    this.form.get('recurringBasis')?.valueChanges.subscribe(() => {
      this.calculateDueNext();
    });
    this.form.get('recurringBasisOtherInterval')?.valueChanges.subscribe(() => {
      this.calculateDueNext();
    });
    this.form.get('yearsToForcastStockQuantity')?.valueChanges.subscribe(() => {
      this.recalculateAllStockForecasts();
    });

    this.form.get('assetId')?.valueChanges.subscribe((assetId) => {
      this.updateSelectedAssetList(assetId);
    });
  }

  /**
   * Updates the selected asset list displayed in the General tab's grid.
   * @param assetId Selected asset ID or null.
   */
  updateSelectedAssetList(assetId: number | null): void {
    if (!assetId) {
      this._selectedAssetList = [];
      return;
    }
    const found = this.assets?.find((a) => a.id === assetId);
    if (found) {
      this._selectedAssetList = [found];
    } else {
      this.assetService.getAssetById(assetId).subscribe({
        next: (res: any) => {
          const asset = res?.data || res?.body || res;
          if (asset && asset.id) {
            this._selectedAssetList = [asset];
          }
        }
      });
    }
  }

  /**
   * Clears the selected asset when removed from the Selected Asset grid.
   */
  _clearSelectedAsset(): void {
    this.form.patchValue({ assetId: null });
    this._selectedAssetList = [];
  }

  /**
   * Populates the form controls from an existing MaintenanceAssetScheduleDto.
   * @param schedule The schedule DTO to patch into the form.
   */
  private patchFormWithSchedule(schedule: any): void {
    if (!schedule || !this.form) return;

    if (schedule.id) {
      this._model = { id: schedule.id };
    }

    const isRec = Boolean(schedule.isRecurring);
    const isTime = schedule.timeInterval !== false;

    const parseDate = (d: any): Date | null => {
      if (!d) return null;
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const scheduledForDate = parseDate(schedule.scheduledFor);
    const startDateDate = parseDate(schedule.startDate);
    const endDateDate = parseDate(schedule.endDate);
    const recurringBasisDate = parseDate(schedule.recurringBasis);
    const dueNextDate = parseDate(schedule.dueNext);

    if (scheduledForDate && scheduledForDate < this._minDateToday) {
      this._minDateToday = scheduledForDate;
    }
    if (startDateDate && startDateDate < this._minDateToday) {
      this._minDateToday = startDateDate;
    }

    let maintIntervalVal = schedule.maintIntervalType;
    if (typeof maintIntervalVal === 'string') {
      const match = this.intervalTypes.find(
        (t) =>
          t.name.toLowerCase() === (maintIntervalVal as string).toLowerCase() ||
          t.code.toLowerCase() === (maintIntervalVal as string).toLowerCase()
      );
      if (match) maintIntervalVal = match.id;
    }
    if (!maintIntervalVal) {
      maintIntervalVal = MaintIntervalType.Days;
    }

    this.form.patchValue(
      {
        id: schedule.id || null,
        maintenanceCode: schedule.maintenanceCode || '',
        assetId: schedule.assetId || null,
        maintenanceTypeId: schedule.maintenanceTypeId || null,
        personId: schedule.personId || null,
        isRecurring: isRec,
        timeInterval: isTime,
        scheduledFor: scheduledForDate || (isRec ? null : new Date()),
        scheduledForOtherInterval: schedule.scheduledForOtherInterval ?? null,
        frequency: schedule.frequency ?? 1,
        maintenanceIntervalId: schedule.maintenanceIntervalId || null,
        maintIntervalType: maintIntervalVal,
        scheduledBased: schedule.scheduledBased ?? false,
        startDate: startDateDate || (isRec ? new Date() : null),
        endDate: endDateDate,
        recurringBasis: recurringBasisDate || startDateDate || (isRec ? new Date() : null),
        recurringBasisOtherInterval: schedule.recurringBasisOtherInterval ?? null,
        dueNext: dueNextDate,
        dueNextOthers: schedule.dueNextOthers ?? null,
        yearsToForcastStockQuantity: schedule.yearsToForcastStockQuantity ?? 1,
        isFlagged: Boolean(schedule.isFlagged),
        notes: schedule.notes || ''
      },
      { emitEvent: false }
    );

    if (schedule.assetId) {
      this.updateSelectedAssetList(schedule.assetId);
    }

    if (schedule.stockParts && schedule.stockParts.length > 0) {
      this.stockParts = schedule.stockParts.map((s: any) => ({ ...s }));
    } else {
      this.stockParts = [];
    }

    if (schedule.files && schedule.files.length > 0) {
      this._scheduleFilesDoc = schedule.files.map((f: any) => ({
        fileId: f.id,
        fileid: f.id,
        id: f.id,
        file: f,
        name: f.name,
        description: f.description,
        type: f.type,
        stream: f.stream
      }));
      this._selectedDocumentsKeys = this._scheduleFilesDoc.map((f: any) => f.fileId);
    } else if (schedule.documentFileIds && schedule.documentFileIds.length > 0) {
      this._scheduleFilesDoc = schedule.documentFileIds.map((id: number) => ({
        fileId: id,
        fileid: id,
        id: id,
        name: `Document #${id}`
      }));
      this._selectedDocumentsKeys = [...schedule.documentFileIds];
    } else {
      this._scheduleFilesDoc = [];
      this._selectedDocumentsKeys = [];
    }

    this.updateTimingValidatorsAndCalculations(false);
  }

  /**
   * Dynamically toggles required validators depending on mode and time interval selection:
   * - One-time + Date-based: scheduledFor is required.
   * - One-time + Asset-field-based: scheduledForOtherInterval and maintenanceIntervalId are required.
   * - Recurring + Date-based: frequency, maintIntervalType, startDate are required.
   * - Recurring + Asset-field-based: frequency, maintenanceIntervalId, recurringBasisOtherInterval are required.
   */
  updateTimingValidatorsAndCalculations(recalculate: boolean = true): void {
    const isRecurring = Boolean(this.form.get('isRecurring')?.value);
    const timeInterval = this.form.get('timeInterval')?.value !== false;

    const scheduledFor = this.form.get('scheduledFor');
    const scheduledForOtherInterval = this.form.get('scheduledForOtherInterval');
    const frequency = this.form.get('frequency');
    const maintIntervalType = this.form.get('maintIntervalType');
    const maintenanceIntervalId = this.form.get('maintenanceIntervalId');
    const startDate = this.form.get('startDate');
    const recurringBasis = this.form.get('recurringBasis');
    const recurringBasisOtherInterval = this.form.get('recurringBasisOtherInterval');

    const nowInUserTz = this._sharedService?.getTodayInUserTimezone
      ? this._sharedService.getTodayInUserTimezone()
      : new Date();

    if (!isRecurring) {
      frequency?.clearValidators();
      maintIntervalType?.clearValidators();
      startDate?.clearValidators();
      recurringBasis?.clearValidators();
      recurringBasisOtherInterval?.clearValidators();

      if (timeInterval) {
        scheduledFor?.setValidators([Validators.required]);
        scheduledForOtherInterval?.clearValidators();
        maintenanceIntervalId?.clearValidators();
        if (!scheduledFor?.value && !this.isEdit) {
          scheduledFor?.setValue(nowInUserTz);
        }
      } else {
        scheduledFor?.clearValidators();
        scheduledForOtherInterval?.setValidators([Validators.required, Validators.min(0)]);
        maintenanceIntervalId?.setValidators([Validators.required]);
      }
    } else {
      scheduledFor?.clearValidators();
      scheduledForOtherInterval?.clearValidators();

      if (timeInterval) {
        frequency?.setValidators([Validators.required, Validators.min(1)]);
        maintIntervalType?.setValidators([Validators.required]);
        startDate?.setValidators([Validators.required]);
        maintenanceIntervalId?.clearValidators();
        recurringBasisOtherInterval?.clearValidators();

        if (!frequency?.value) frequency?.setValue(1);
        if (!maintIntervalType?.value) maintIntervalType?.setValue(MaintIntervalType.Days);
        if (!startDate?.value && !this.isEdit) startDate?.setValue(nowInUserTz);
        if (!recurringBasis?.value && !this.isEdit) recurringBasis?.setValue(startDate?.value || nowInUserTz);
      } else {
        frequency?.setValidators([Validators.required, Validators.min(1)]);
        maintenanceIntervalId?.setValidators([Validators.required]);
        startDate?.clearValidators();
        maintIntervalType?.clearValidators();
        recurringBasis?.clearValidators();
        recurringBasisOtherInterval?.setValidators([Validators.required, Validators.min(0)]);

        if (!frequency?.value) frequency?.setValue(1);
      }
    }

    scheduledFor?.updateValueAndValidity();
    scheduledForOtherInterval?.updateValueAndValidity();
    frequency?.updateValueAndValidity();
    maintIntervalType?.updateValueAndValidity();
    maintenanceIntervalId?.updateValueAndValidity();
    startDate?.updateValueAndValidity();
    recurringBasis?.updateValueAndValidity();
    recurringBasisOtherInterval?.updateValueAndValidity();

    if (recalculate) {
      this.calculateDueNext();
    }
    this.recalculateAllStockForecasts();
  }

  /**
   * Recalculates dueNext date or dueNextOthers number based on recurrence inputs.
   */
  calculateDueNext(): void {
    const isRecurring = Boolean(this.form.get('isRecurring')?.value);
    const timeInterval = this.form.get('timeInterval')?.value !== false;
    const freq = Number(this.form.get('frequency')?.value) || 0;

    if (!isRecurring) {
      return;
    }

    if (timeInterval) {
      const basisDateVal = this.form.get('recurringBasis')?.value || this.form.get('startDate')?.value;
      if (basisDateVal && freq > 0) {
        const intervalType = this.form.get('maintIntervalType')?.value;
        const next = new Date(basisDateVal);
        if (intervalType === MaintIntervalType.Days || intervalType === 'Days' || intervalType === 1) {
          next.setDate(next.getDate() + freq);
        } else if (intervalType === MaintIntervalType.Week || intervalType === 'Week' || intervalType === 5) {
          next.setDate(next.getDate() + freq * 7);
        } else if (intervalType === MaintIntervalType.Months || intervalType === 'Months' || intervalType === 2) {
          next.setMonth(next.getMonth() + freq);
        } else if (intervalType === MaintIntervalType.Years || intervalType === 'Years' || intervalType === 4) {
          next.setFullYear(next.getFullYear() + freq);
        } else if (intervalType === MaintIntervalType.Hours || intervalType === 'Hours' || intervalType === 3) {
          next.setHours(next.getHours() + freq);
        } else {
          next.setDate(next.getDate() + freq);
        }
        this.form.patchValue({ dueNext: next }, { emitEvent: false });
      }
    } else {
      const basisNum = Number(this.form.get('recurringBasisOtherInterval')?.value) || 0;
      this.form.patchValue({ dueNextOthers: basisNum + freq }, { emitEvent: false });
    }
  }

  /**
   * Recalculates demand forecasts for all added stock items.
   */
  recalculateAllStockForecasts(): void {
    if (this.stockParts && this.stockParts.length > 0) {
      this.stockParts.forEach((p) => {
        p.forecastedQuantity = this.calculateForecast(p);
      });
    }
  }

  /**
   * Fetches active maintenance types for the dropdown list.
   */
  loadMaintenanceTypes(): void {
    this.scheduleService.getMaintenanceTypes().subscribe({
      next: (types: any) => {
        const raw = Array.isArray(types) ? types : (types?.data || []);
        this.maintenanceTypes = raw;
        this.maintenanceTypesFiltered = [...this.maintenanceTypes];
      }
    });
  }

  /**
   * Orchestrates asynchronous retrieval of all lookup datasets:
   * 1. Maintenance Types
   * 2. Maintenance Intervals
   * 3. Active Persons
   * 4. Assets via initial page of getAssetsDD()
   */
  private loadDropdownData(): void {
    // 1. Maintenance Types
    this.loadMaintenanceTypes();

    // 2. Maintenance Intervals
    this.scheduleService.getMaintenanceIntervals().subscribe({
      next: (intervals: any) => {
        const rawList = Array.isArray(intervals) ? intervals : (intervals?.data || []);
        if (rawList && rawList.length > 0) {
          this.intervals = rawList.map((i: any) => ({
            ...i,
            id: i.id,
            name: i.maintenanceIntervalName || i.name,
            code: i.maintenanceIntervalCode || i.code,
            maintenanceIntervalName: i.maintenanceIntervalName || i.name
          }));
        } else {
          this.intervals = this.getDefaultIntervals();
        }
        this.intervalsFiltered = [...this.intervals];

        if (this.form.get('timeInterval')?.value === false && !this.form.get('maintenanceIntervalId')?.value && this.intervals.length > 0) {
          const defaultInterval =
            this.intervals.find((i: any) => (i.code || i.name)?.toLowerCase().includes('day')) ||
            this.intervals[0];
          this.form.patchValue({ maintenanceIntervalId: defaultInterval.id });
        }
      },
      error: () => {
        this.intervals = this.getDefaultIntervals();
        this.intervalsFiltered = [...this.intervals];
        if (this.form.get('timeInterval')?.value === false && !this.form.get('maintenanceIntervalId')?.value && this.intervals.length > 0) {
          this.form.patchValue({ maintenanceIntervalId: this.intervals[0].id });
        }
      }
    });

    // 2b. Recurring Interval Types (Lookup of Enums)
    this.scheduleService.getMaintIntervalTypes().subscribe({
      next: (types: any) => {
        const rawList = Array.isArray(types) ? types : (types?.data || []);
        if (rawList && rawList.length > 0) {
          this.intervalTypes = rawList;
          this.intervalTypesFiltered = [...this.intervalTypes];
        }
      },
      error: () => {}
    });

    // 3. Persons (Active Persons mapped with display names)
    this.usersService.getActivePersons().subscribe({
      next: (res: any) => {
        const raw = res?.body || res?.data || res || [];
        this.persons = (Array.isArray(raw) ? raw : []).map((p: any) => ({
          ...p,
          displayName: `${p.code ? p.code + ' - ' : ''}${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || '',
          fullName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || ''
        }));
        this.personsFiltered = [...this.persons];

        if (this.schedule?.personId) {
          this.ensurePersonLoaded(this.schedule.personId);
        }
      }
    });

    // 4. Assets (using standard DropDownListPagingParams)
    this.getAssetsDD();
  }

  /**
   * Fetches assets page-by-page for the Kendo ComboBox.
   * - If not scrolling (_assetComboScroll = false): initializes list and preserves active selection.
   * - If scrolling (_assetComboScroll = true): concatenates incoming page, filtering out duplicate IDs.
   */
  getAssetsDD(): void {
    if (this._isLoadingAssets) return;
    this._isLoadingAssets = true;
    this.assetService.getAssetList(this._assetComboBoxPagingParams).subscribe({
      next: (res: any) => {
        this._isLoadingAssets = false;
        this._allAssetCount = res?.body?.returnCount ?? 0;
        const incoming = res?.body?.data || res?.body || [];
        if (!this._assetComboScroll) {
          const currentId = this.form?.get('assetId')?.value;
          const currentAsset = currentId ? this.assets.find((a) => a.id === currentId) : null;
          if (currentAsset && !incoming.some((a: any) => a.id === currentId)) {
            this.assets = [currentAsset, ...incoming];
          } else {
            this.assets = [...incoming];
          }
          this.assetsFiltered = [...this.assets];
          if (currentId && this._selectedAssetList.length === 0) {
            this.updateSelectedAssetList(currentId);
          }
        } else {
          const existingIds = new Set(this.assets.map((a) => a.id));
          const newItems = incoming.filter((a: any) => !existingIds.has(a.id));
          this.assets = this.assets.concat(newItems);
          this.assetsFiltered = [...this.assets];
        }

        if (this.schedule?.assetId) {
          this.ensureAssetLoaded(this.schedule.assetId);
        }
      },
      error: () => {
        this._isLoadingAssets = false;
      }
    });
  }

  /**
   * Triggered when the Asset Kendo ComboBox opens.
   * Uses Renderer2 to attach an active scroll listener to the popup container (`.k-popup .k-list-content`).
   * When scrolled within 15px of the bottom, increments page and fetches the next batch.
   * @param event Kendo open event.
   */
  scrollCombobox(event: any): void {
    this.isOpened = true;
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    this.clearTimeout = setTimeout(() => {
      if (this.isOpened) {
        const domobject: any =
          document.querySelector('.k-popup .k-list-content') ||
          document.querySelector('.k-list-content') ||
          document.querySelector('.k-list-scroller');
        if (domobject) {
          if (this._scrollUnlisten) {
            this._scrollUnlisten();
            this._scrollUnlisten = undefined;
          }
          this._scrollUnlisten = this._renderer2.listen(domobject, 'scroll', (e: any) => {
            if (!this._isLoadingAssets && this.assets.length < this._allAssetCount) {
              if (
                e.target.offsetHeight + e.target.scrollTop >= e.target.scrollHeight - 15 ||
                e.target.clientHeight + e.target.scrollTop >= e.target.scrollHeight - 15
              ) {
                this._assetComboScroll = true;
                this._assetComboBoxPagingParams.currentItem = (
                  parseInt(this._assetComboBoxPagingParams.currentItem) +
                  this._assetComboBoxPagingParams.totalbyPage
                ).toString();
                this._assetComboBoxPagingParams.page++;
                this.getAssetsDD();
              }
            }
          });
        }
      }
    }, 500);
  }

  /**
   * Cleans up listener and resets open state when the Asset ComboBox popup closes.
   */
  onComboboxClosed(): void {
    this.isOpened = false;
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    if (this._scrollUnlisten) {
      this._scrollUnlisten();
      this._scrollUnlisten = undefined;
    }
  }

  /**
   * Ensures the asset specified by ID exists in the dropdown options.
   * If missing (e.g. on higher pages), fetches by ID and prepends to the array.
   * @param assetId ID of the asset to verify.
   */
  ensureAssetLoaded(assetId: number): void {
    if (!assetId) return;
    if (!this.assets.some((a) => a.id === assetId)) {
      this.assetService.getAssetById(assetId).subscribe({
        next: (res: any) => {
          const asset = res?.data || res?.body || res;
          if (asset && asset.id) {
            this.assets.unshift(asset);
            this.assetsFiltered = [...this.assets];
            this.form.patchValue({ assetId: asset.id });
            this._selectedAssetList = [asset];
          }
        }
      });
    } else {
      const found = this.assets.find((a) => a.id === assetId);
      if (found) {
        this._selectedAssetList = [found];
      }
    }
  }

  /**
   * Ensures the person specified by ID exists in the persons dropdown options.
   * @param personId ID of the person to verify.
   */
  ensurePersonLoaded(personId: number): void {
    if (!personId) return;
    if (!this.persons.some((p) => p.id === personId)) {
      this.usersService.getPersonById(personId).subscribe({
        next: (res: any) => {
          const p = res?.data || res?.body || res;
          if (p && p.id) {
            const mapped = {
              ...p,
              displayName: `${p.code ? p.code + ' - ' : ''}${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || '',
              fullName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || ''
            };
            this.persons.unshift(mapped);
            this.personsFiltered = [...this.persons];
            this.form.patchValue({ personId: p.id });
          }
        }
      });
    }
  }

  /**
   * Debounced filter handler for Asset ComboBox search input.
   * Resets paging parameters to page 1 and performs a backend query.
   * @param value Search query entered by user.
   */
  handleAssetFilter(value: string): void {
    this._assetComboScroll = false;
    if (this._assetFilterTimeout) clearTimeout(this._assetFilterTimeout);
    this._assetFilterTimeout = setTimeout(() => {
      const term = value ? value.trim() : '';
      this._assetComboBoxPagingParams.search = term;
      this._assetComboBoxPagingParams.currentItem = '0';
      this._assetComboBoxPagingParams.page = 1;
      this.getAssetsDD();
    }, 500);
  }

  /**
   * In-memory filter handler for Assigned Person ComboBox.
   * @param value Filter string.
   */
  handlePersonFilter(value: string): void {
    if (!value) {
      this.personsFiltered = [...this.persons];
    } else {
      const term = value.toLowerCase().trim();
      this.personsFiltered = this.persons.filter(
        (p) =>
          (p.code && p.code.toLowerCase().includes(term)) ||
          (p.firstName && p.firstName.toLowerCase().includes(term)) ||
          (p.lastName && p.lastName.toLowerCase().includes(term)) ||
          (p.displayName && p.displayName.toLowerCase().includes(term)) ||
          (p.fullName && p.fullName.toLowerCase().includes(term))
      );
    }
  }

  /**
   * In-memory filter handler for Maintenance Type ComboBox.
   * @param value Filter string.
   */
  handleMaintenanceTypeFilter(value: string): void {
    if (!value) {
      this.maintenanceTypesFiltered = [...this.maintenanceTypes];
    } else {
      const term = value.toLowerCase().trim();
      this.maintenanceTypesFiltered = this.maintenanceTypes.filter(
        (t) =>
          (t.maintenanceTypeName && t.maintenanceTypeName.toLowerCase().includes(term)) ||
          (t.maintenanceTypeCode && t.maintenanceTypeCode.toLowerCase().includes(term))
      );
    }
  }

  /**
   * Fallback maintenance intervals / asset data fields matching standard configuration.
   */
  private getDefaultIntervals(): any[] {
    return [
      { id: 1, code: 'Licenses', name: 'Licenses', maintenanceIntervalName: 'Licenses' },
      { id: 2, code: 'MaintContractCost', name: 'Maint. Contract Cost', maintenanceIntervalName: 'Maint. Contract Cost' },
      { id: 3, code: 'Mileage', name: 'Mileage', maintenanceIntervalName: 'Mileage' },
      { id: 4, code: 'ScrapValue', name: 'Scrap Value', maintenanceIntervalName: 'Scrap Value' },
      { id: 5, code: 'number', name: 'number', maintenanceIntervalName: 'number' },
      { id: 6, code: 'Num', name: 'Num', maintenanceIntervalName: 'Num' },
      { id: 7, code: 'DEF', name: 'DEF', maintenanceIntervalName: 'DEF' },
      { id: 8, code: 'SDW', name: 'SDW', maintenanceIntervalName: 'SDW' },
      { id: 9, code: 'ASQW1', name: 'ASQW1', maintenanceIntervalName: 'ASQW1' },
      { id: 10, code: 'ASQWS', name: 'ASQWS', maintenanceIntervalName: 'ASQWS' }
    ];
  }

  /**
   * In-memory filter handler for Maintenance Interval ComboBox.
   * @param value Filter string.
   */
  handleIntervalFilter(value: string): void {
    if (!value) {
      this.intervalsFiltered = [...this.intervals];
    } else {
      const term = value.toLowerCase().trim();
      this.intervalsFiltered = this.intervals.filter(
        (i) =>
          (i.name && i.name.toLowerCase().includes(term)) ||
          (i.code && i.code.toLowerCase().includes(term)) ||
          (i.maintenanceIntervalName && i.maintenanceIntervalName.toLowerCase().includes(term))
      );
    }
  }

  /**
   * In-memory filter handler for Recurring Interval Type ComboBox (lookup of enums).
   * @param value Filter string.
   */
  handleIntervalTypeFilter(value: string): void {
    if (!value) {
      this.intervalTypesFiltered = [...this.intervalTypes];
    } else {
      const term = value.toLowerCase().trim();
      this.intervalTypesFiltered = this.intervalTypes.filter(
        (t) =>
          (t.name && t.name.toLowerCase().includes(term)) ||
          (t.code && t.code.toLowerCase().includes(term))
      );
    }
  }

  // #region Asset Popup Selector

  /**
   * Opens the full asset grid selector dialog.
   */
  _openAssetsList(): void {
    this._assetsListEnabled = true;
  }

  /**
   * Closes the full asset grid selector dialog.
   */
  _closeAssetsList(): void {
    this._assetsListEnabled = false;
  }

  /**
   * Applies the asset selected in the grid popup dialog to the form.
   */
  _returnSelectedAssetsHandler(e?: any): void {
    if (this._selectedAssets && this._selectedAssets.length > 0) {
      const selectedId = this._selectedAssets[0];
      this.ensureAssetLoaded(selectedId);
      this.form.patchValue({ assetId: selectedId });
    }
    this._closeAssetsList();
  }

  _onAssetsRowSelectionChanges(e: any[]): void {
    this._selectedAssets = [...e];
  }

  _getAssets = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this.assetService.getAssetGrid(params, model);
  };
  // #endregion

  // #region Person Popup Selector

  /**
   * Opens the full person grid selector dialog.
   */
  _openPersonsList(): void {
    this._personsListEnabled = true;
  }

  /**
   * Closes the full person grid selector dialog.
   */
  _closePersonsList(): void {
    this._personsListEnabled = false;
  }

  /**
   * Applies the person selected in the grid popup dialog to the form.
   */
  _returnSelectedPersonsHandler(e?: any): void {
    if (this._selectedPerson) {
      this.ensurePersonLoaded(this._selectedPerson);
      this.form.patchValue({ personId: this._selectedPerson });
    }
    this._closePersonsList();
  }

  _onPersonsRowSelectionChanges(e: any[]): void {
    this._selectedPerson = e[0];
  }

  _getPersons = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this.usersService.getPersonsGridData(params, model);
  };
  // #endregion

  // #region Stock Items Popup Selector & Parts Management
  _openStockItemsList(): void {
    this._stockItemsListEnabled = true;
  }

  _closeStockItemsList(): void {
    this._stockItemsListEnabled = false;
  }

  _onStockItemsRowSelectionChanges(e: any[]): void {
    this._selectedStockItems = [...e];
  }

  _returnSelectedStockItemsHandler(e?: any): void {
    if (this._selectedStockItems && this._selectedStockItems.length > 0) {
      const siteId = this.form.get('siteId')?.value || undefined;
      this._selectedStockItems.forEach((stockId: any) => {
        const idNum = Number(stockId);
        if (!this.stockParts.some((p) => p.stockId === idNum)) {
          this._stockItemsService.getStockItem(idNum).subscribe({
            next: (data: any) => {
              if (data) {
                const costVal =
                  data.costInfo?.cost != null
                    ? Number(data.costInfo.cost)
                    : data.cost != null
                    ? Number(data.cost)
                    : 0;
                const part: MaintenanceScheduleStockDto = {
                  stockId: data.id,
                  stockNumber: data.number,
                  stockDescription: data.description,
                  requiredQuantity: 1,
                  cost: costVal,
                  extendedCost: costVal,
                  unitOfMeasureCode: data.inventoryUnitOfMeasure?.code || data.uomunitcode,
                  unitOfMeasureName: data.inventoryUnitOfMeasure?.unitName,
                  siteId: siteId
                };
                part.forecastedQuantity = this.calculateForecast(part);
                this.stockParts.push(part);
                this.stockParts = [...this.stockParts];
              }
            }
          });
        }
      });
    }
    this._closeStockItemsList();
  }

  _getStockItems = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this._stockItemsService.getStockItems(params, model);
  };

  removeStockPart(index: number): void {
    if (index >= 0 && index < this.stockParts.length) {
      this.stockParts.splice(index, 1);
      this.stockParts = [...this.stockParts];
    }
  }

  onPartCostOrQtyChange(part: MaintenanceScheduleStockDto): void {
    const qty = Number(part.requiredQuantity) || 0;
    const cost = Number(part.cost) || 0;
    part.extendedCost = Math.round(qty * cost * 100) / 100;
    part.forecastedQuantity = this.calculateForecast(part);
  }

  get totalPartsCost(): number {
    return this.stockParts.reduce(
      (sum, p) =>
        sum +
        (p.extendedCost != null
          ? Number(p.extendedCost)
          : (Number(p.requiredQuantity) || 0) * (Number(p.cost) || 0)),
      0
    );
  }

  calculateForecast(part: MaintenanceScheduleStockDto): number | undefined {
    const isRecurring = this.form.get('isRecurring')?.value;
    if (!isRecurring) return undefined;
    const freq = Number(this.form.get('frequency')?.value) || 1;
    const intervalType = this.form.get('maintIntervalType')?.value;
    const years = Number(this.form.get('yearsToForcastStockQuantity')?.value) || 1;

    let intervalDays = 0;
    if (intervalType === MaintIntervalType.Days || intervalType === 'Days' || intervalType === 1) {
      intervalDays = freq;
    } else if (intervalType === MaintIntervalType.Week || intervalType === 'Week' || intervalType === 5) {
      intervalDays = freq * 7;
    } else if (intervalType === MaintIntervalType.Months || intervalType === 'Months' || intervalType === 2) {
      intervalDays = freq * 30.4375;
    } else if (intervalType === MaintIntervalType.Years || intervalType === 'Years' || intervalType === 4) {
      intervalDays = freq * 365.25;
    } else if (intervalType === MaintIntervalType.Hours || intervalType === 'Hours' || intervalType === 3) {
      intervalDays = freq / 24;
    } else {
      intervalDays = freq * 30.4375;
    }

    const qty = Number(part.requiredQuantity) || 0;
    if (intervalDays > 0 && qty > 0) {
      return Math.round(qty * (365.25 / intervalDays) * years * 100) / 100;
    }
    return undefined;
  }
  // #endregion

  /**
   * Validates and submits the schedule maintenance form payload.
   * Handles both CREATE and UPDATE workflows.
   * @param action 'save' to stay on form in edit mode, or 'saveAndClose' to close dialog.
   */
  _onSave(action: 'save' | 'saveAndClose' = 'saveAndClose'): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.layoutComponent.openPopup({
        title: 'Validation Error',
        message: 'Please fill in all required fields marked with *.',
        withoutConfirm: true
      });
      return;
    }

    this.isSaving = true;
    const val = this.form.value;
    const isRec = Boolean(val.isRecurring);
    const isTime = val.timeInterval !== false;

    const model: MaintenanceAssetScheduleRequestModel = {
      id: val.id || undefined,
      maintenanceCode: val.maintenanceCode || this.schedule?.maintenanceCode,
      assetId: Number(val.assetId),
      maintenanceTypeId: val.maintenanceTypeId ? Number(val.maintenanceTypeId) : undefined,
      personId: val.personId ? Number(val.personId) : undefined,
      isRecurring: isRec,
      timeInterval: isTime,
      scheduledFor: !isRec && isTime ? val.scheduledFor : undefined,
      scheduledForOtherInterval: !isRec && !isTime && val.scheduledForOtherInterval != null ? Number(val.scheduledForOtherInterval) : undefined,
      frequency: isRec && val.frequency ? Number(val.frequency) : undefined,
      maintenanceIntervalId: (!isTime && val.maintenanceIntervalId) ? Number(val.maintenanceIntervalId) : undefined,
      maintIntervalType: isRec && isTime && val.maintIntervalType ? val.maintIntervalType : undefined,
      scheduledBased: isRec ? Boolean(val.scheduledBased) : undefined,
      startDate: isRec && isTime ? val.startDate : (isRec && val.startDate ? val.startDate : undefined),
      endDate: isRec && val.endDate ? val.endDate : undefined,
      recurringBasis: isRec && isTime && val.recurringBasis ? val.recurringBasis : undefined,
      recurringBasisOtherInterval: isRec && !isTime && val.recurringBasisOtherInterval != null ? Number(val.recurringBasisOtherInterval) : undefined,
      dueNext: isRec && isTime && val.dueNext ? val.dueNext : (!isRec && isTime ? val.scheduledFor : undefined),
      dueNextOthers: isRec && !isTime && val.dueNextOthers != null ? Number(val.dueNextOthers) : undefined,
      yearsToForcastStockQuantity: Number(val.yearsToForcastStockQuantity) || 1,
      isFlagged: Boolean(val.isFlagged),
      notes: val.notes || '',
      documentFileIds:
        this._scheduleFilesDoc
          ?.map((f: any) => Number(f.fileId || f.fileid || f.id || f.file?.id))
          .filter((id: number) => !isNaN(id) && id > 0) || [],
      stockParts: this.stockParts.map((p) => ({
        id: p.id,
        stockId: p.stockId,
        requiredQuantity: Number(p.requiredQuantity) || 1,
        cost: p.cost != null ? Number(p.cost) : 0,
        extendedCost:
          p.extendedCost != null
            ? Number(p.extendedCost)
            : (Number(p.requiredQuantity) || 1) * (Number(p.cost) || 0)
      }))
    };

    const request$ =
      this.isEdit && val.id
        ? this.scheduleService.updateSchedule(val.id, model)
        : this.scheduleService.createSchedule(model);

    request$.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        const savedSchedule = res?.data || res;
        const savedId = savedSchedule?.id || res?.id || val.id;
        if (this.note && savedId) {
          this.note.createNote(savedId);
        }
        if (savedId) {
          this._model = { id: savedId };
        }

        this.layoutComponent.openPopup({
          title: 'Success',
          message: `Schedule ${this.isEdit ? 'updated' : 'created'} successfully.`,
          withoutConfirm: true
        });
        this.saved.emit(res);
        this._sharedService.ActivateOrDeactivate.emit(true);

        if (action === 'saveAndClose') {
          this._closeForm('saveAndClose', res);
        } else {
          // Stay on form, update to edit mode
          this.isEdit = true;
          this._pageState = PageState.EDIT;
          if (res?.id) {
            this.form.patchValue({ id: res.id });
            this.schedule = { ...this.schedule, ...res };
          }
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        const msg =
          err?.error?.data?.message || err?.error?.message || err?.message || 'Error saving maintenance schedule.';
        this.layoutComponent.openPopup({
          title: 'Error',
          message: msg,
          withoutConfirm: true
        });
      }
    });
  }

  /**
   * Emits cancellation and dialog close events through SharedService.
   */
  _closeForm(action?: string, response?: any): void {
    const closeComponent = {
      isClosed: true,
      open: false,
      component: 'schedule-maintenance',
      action: action,
      response: response
    };
    this._sharedService.closeDialog.emit(closeComponent);
  }

  /**
   * Closes the form upon Cancel or Close button click.
   */
  onCancel(): void {
    this._closeForm('close');
  }

  /**
   * Opens dialog to create a new Maintenance Type.
   */
  openAddType(): void {
    this.openUpsert(PageNames.MaintenanceType, null, PageState.ADD);
  }

  /**
   * Opens dialog to edit the currently selected Maintenance Type.
   */
  openEditSelectedType(): void {
    const selectedId = this.form.get('maintenanceTypeId')?.value;
    if (!selectedId) return;

    const existing = this.maintenanceTypes.find((t) => t.id === selectedId);
    if (existing) {
      this.openUpsert(PageNames.MaintenanceType, existing, PageState.EDIT);
    }
  }

  /**
   * Launches an inline upsert dialog (e.g. MaintenanceType).
   * @param componentName Target component name.
   * @param model Payload to pass to dialog.
   * @param pageState ADD or EDIT.
   */
  openUpsert(componentName: string, model: any, pageState: PageState): void {
    this._dialogComponentName = componentName;
    this._dialogModel = { model, pageState };
    this._isDialiog = true;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    const windowSize = window.innerWidth;
    if (windowSize <= 991) {
      this._kendoTabStripPosition = 'top';
    } else {
      this._kendoTabStripPosition = 'left';
    }
  }

  _closeCustomFields(): void {}

  openAddDocument(): void {
    this._dialogComponentName = 'documents';
    this._isDialiog = true;
    this._dialogModel = {
      model: {},
      pageState: PageState.ADD,
      isDocument: true,
      reload: 'model',
      fileSource: fileSources.documents
    };
  }

  _onTabSelect(e: any): void {
    if (e?.title) {
      this._selectedTab = e.title;
    } else if (e?.index === 0) {
      this._selectedTab = 'Documents';
    } else if (e?.index === 1) {
      this._selectedTab = 'StockParts';
    }
  }

  /**
   * Handles close events from nested dialogs (e.g. Maintenance Type or Documents modal).
   * Automatically refreshes maintenance types list and selects the newly saved record.
   * @param event Dialog close payload.
   */
  _closeDialog(event: any): void {
    this._isDialiog = false;
    this._dialogComponentName = '';
    this._dialogModel = null;

    if (event?.response && (event.component === PageNames.MaintenanceType || event.component === 'maintenance-type')) {
      const savedType: MaintenanceTypeDto = event.response;
      if (savedType && savedType.id) {
        const idx = this.maintenanceTypes.findIndex((t) => t.id === savedType.id);
        if (idx >= 0) {
          this.maintenanceTypes[idx] = savedType;
          this.maintenanceTypes = [...this.maintenanceTypes];
        } else {
          this.maintenanceTypes = [...this.maintenanceTypes, savedType];
        }
        this.form.patchValue({ maintenanceTypeId: savedType.id });
      }
    }

    this.loadMaintenanceTypes();
  }
}
